// fiscal-api — wrapper Axios pros endpoints fiscais (Epic 10).
// Manual emit / cancel / list / detail / DLQ reprocess+discard.

import { z } from 'zod';

import { apiClient } from './api-client';

export type FiscalInvoiceStatus =
  | 'pending'
  | 'issuing'
  | 'issued'
  | 'failed'
  | 'cancelled'
  | 'pending_data'
  | 'discarded';

// Backend retorna Prisma raw (camelCase) — não temos transform layer.
export const fiscalInvoiceSchema = z.object({
  id: z.string().uuid(),
  chargeId: z.string().uuid(),
  clientId: z.string().uuid(),
  subscriptionId: z.string().uuid().nullable(),
  saasProductId: z.string().uuid().nullable(),
  status: z.string(),
  invoiceNumber: z.string().nullable(),
  serviceDescription: z.string().nullable(),
  amount: z.string(),
  competence: z.string(),
  emissionDate: z.string().nullable(),
  provider: z.string(),
  externalId: z.string().nullable(),
  pdfUrl: z.string().nullable(),
  xmlUrl: z.string().nullable(),
  issRate: z.string().nullable(),
  issAmount: z.string().nullable(),
  takerDocument: z.string().nullable(),
  takerDocumentWasOverride: z.boolean(),
  // Nome do tomador (cliente) — pra tabela/drawer não mostrar só o documento.
  client: z.object({ name: z.string(), tradeName: z.string().nullable() }).nullable().optional(),
  retencaoTotal: z.string().nullable(),
  retencaoBreakdown: z.unknown().nullable(),
  errorMessage: z.string().nullable(),
  lastErrorCode: z.string().nullable(),
  retryCount: z.number(),
  createdVia: z.string(),
  cancelledReason: z.string().nullable(),
  cancelledByUserId: z.string().uuid().nullable(),
  cancelledAt: z.string().nullable(),
  issuedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type FiscalInvoice = z.infer<typeof fiscalInvoiceSchema>;

// Detail inclui charge + events timeline
export const fiscalInvoiceEventSchema = z.object({
  id: z.string().uuid(),
  eventType: z.string(),
  payload: z.unknown(),
  triggeredByUserId: z.string().uuid().nullable(),
  correlationId: z.string().nullable(),
  occurredAt: z.string(),
});
export type FiscalInvoiceEvent = z.infer<typeof fiscalInvoiceEventSchema>;

export const fiscalInvoiceDetailSchema = fiscalInvoiceSchema.extend({
  charge: z
    .object({
      id: z.string().uuid(),
      amount: z.string(),
      status: z.string(),
    })
    .nullable()
    .optional(),
  events: z.array(fiscalInvoiceEventSchema).optional().default([]),
});
export type FiscalInvoiceDetail = z.infer<typeof fiscalInvoiceDetailSchema>;

const listResponseSchema = z.object({
  data: z.array(fiscalInvoiceSchema),
  pagination: z.object({
    page: z.number(),
    pageSize: z.number(),
    total: z.number(),
  }),
});

export interface FiscalInvoiceFilters {
  status?: FiscalInvoiceStatus | FiscalInvoiceStatus[];
  client_id?: string;
  from_date?: string;
  to_date?: string;
  saas_product_id?: string;
  charge_id?: string;
  page?: number;
  pageSize?: number;
}

export async function listFiscalInvoices(
  filters: FiscalInvoiceFilters = {},
  signal?: AbortSignal,
): Promise<z.infer<typeof listResponseSchema>> {
  const params: Record<string, string | number> = {};
  if (filters.status) {
    params.status = Array.isArray(filters.status) ? filters.status.join(',') : filters.status;
  }
  if (filters.client_id) params.client_id = filters.client_id;
  if (filters.from_date) params.from_date = filters.from_date;
  if (filters.to_date) params.to_date = filters.to_date;
  if (filters.saas_product_id) params.saas_product_id = filters.saas_product_id;
  if (filters.page) params.page = filters.page;
  if (filters.pageSize) params.pageSize = filters.pageSize;

  const { data } = await apiClient.get('/api/fiscal/invoices', { params, signal });
  return listResponseSchema.parse(data);
}

export async function getFiscalInvoice(
  id: string,
  signal?: AbortSignal,
): Promise<FiscalInvoiceDetail> {
  const { data } = await apiClient.get(`/api/fiscal/invoices/${id}`, { signal });
  return fiscalInvoiceDetailSchema.parse(data.data);
}

// ============================================================================
// Manual emit — Story 10.4
// ============================================================================

export interface ManualEmitPayload {
  charge_id: string;
  taker_document?: string;
  service_description?: string;
  amount?: string;
  emission_date?: string;
  save_permanent?: boolean;
  /** Código NBS escolhido na lista (ex: "1.1103.22.00"). Ausente = NF sem NBS. */
  nbs_code?: string;
}

export interface ManualEmitResult {
  fiscalInvoiceId: string;
  externalInvoiceId: string;
  status: string;
  pdfUrl: string | null;
  xmlUrl: string | null;
}

export async function emitManualFiscalInvoice(
  payload: ManualEmitPayload,
): Promise<ManualEmitResult> {
  const { data } = await apiClient.post('/api/fiscal/invoices/manual', payload);
  return data.data;
}

// ============================================================================
// Standalone emit — NF avulsa (sem cobrança prévia)
// ============================================================================

export interface StandaloneEmitPayload {
  client_id: string;
  amount: string;
  service_description: string;
  emission_date?: string;
  // Natureza fiscal própria (opcional) — trio all-or-nothing; ausente = software padrão.
  municipal_service_code?: string;
  municipal_service_name?: string;
  iss_rate?: number;
  /** Código NBS escolhido na lista (ex: "1.1103.22.00"). Ausente = NF sem NBS. */
  nbs_code?: string;
}

export interface StandaloneEmitResult extends ManualEmitResult {
  chargeId: string;
}

export async function emitStandaloneFiscalInvoice(
  payload: StandaloneEmitPayload,
): Promise<StandaloneEmitResult> {
  const { data } = await apiClient.post('/api/fiscal/invoices/standalone', payload);
  return data.data;
}

// ============================================================================
// NBS — lista oficial do Asaas (proxy em GET /api/fiscal/nbs-codes)
// ============================================================================

/** Mínimo de caracteres pra buscar — o mesmo que a API exige. */
export const NBS_MIN_SEARCH = 2;

const nbsCodeListSchema = z.object({
  data: z.array(z.object({ code: z.string(), description: z.string() })),
});

export type NbsCodeOption = z.infer<typeof nbsCodeListSchema>['data'][number];

export async function searchNbsCodes(
  search: string,
  signal?: AbortSignal,
): Promise<NbsCodeOption[]> {
  const { data } = await apiClient.get('/api/fiscal/nbs-codes', { params: { search }, signal });
  return nbsCodeListSchema.parse(data).data;
}

// ============================================================================
// Cancel — Story 10.5
// ============================================================================

export interface CanCancelResult {
  canCancel: boolean;
  reason?: string;
}

export async function checkCanCancelFiscalInvoice(id: string): Promise<CanCancelResult> {
  const { data } = await apiClient.get(`/api/fiscal/invoices/${id}/can-cancel`);
  return data.data;
}

export async function cancelFiscalInvoice(id: string, reason: string): Promise<void> {
  await apiClient.post(`/api/fiscal/invoices/${id}/cancel`, { reason });
}

// ============================================================================
// DLQ — Story 10.8
// ============================================================================

export async function reprocessFiscalInvoice(id: string): Promise<void> {
  await apiClient.post(`/api/fiscal/invoices/${id}/reprocess`);
}

export async function discardFiscalInvoice(id: string, reason: string): Promise<void> {
  await apiClient.post(`/api/fiscal/invoices/${id}/discard`, { reason });
}

// ============================================================================
// Downloads — Story 10.7
// ============================================================================

export async function downloadFiscalPdf(id: string): Promise<Blob> {
  const { data } = await apiClient.get(`/api/fiscal/invoices/${id}/pdf`, {
    responseType: 'blob',
  });
  return data as Blob;
}

export async function downloadFiscalXml(id: string): Promise<Blob> {
  const { data } = await apiClient.get(`/api/fiscal/invoices/${id}/xml`, {
    responseType: 'blob',
  });
  return data as Blob;
}
